/** @jsxImportSource @emotion/react */
import { useApolloClient, useQuery } from '@apollo/client'
import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import { addMinutes } from 'date-fns'
import { Link } from 'react-router-dom'
import { DocumentNode } from 'graphql'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { FIND_MY_USER } from 'queries/common'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import Table, {
  BulkActionBase,
  BulkActions,
  FiltersWrapper,
  FooterWrapper,
  HeaderWrapper,
  PaginationPage,
  PaginationTake,
} from 'components/Table'
import { Search } from 'components/Input'
import ConfirmationModal from 'components/Modals/ConfirmationModal'
import { findMyUser } from '__generatedTypes__/findMyUser'
import { PromocodeStatusEnum, PromocodeTypeEnum } from '__generatedTypes__/globalTypes'
import { BULK_REMOVE_PROMO_CODES, BULK_UPDATE_PROMO_CODES } from 'queries/promoCode'
import { bulkUpdatePromoCodes, bulkUpdatePromoCodesVariables } from '__generatedTypes__/bulkUpdatePromoCodes'
import { convertToDollars } from 'utils/priceFormatter'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { getTimeZoneAgnosticDate } from 'utils/getTimeZoneAgnosticDate'
import { convertFromSavedPercentage } from '../Form'
import { PromoCode, PromoCodeFilters, usePromoCodes, usePromoCodesCount } from './usePromoCodes'
import * as classes from './styles'

type BulkAction = BulkActionBase & {
  getTitle: (count?: number) => string
  getSubtitle: (count?: number) => string | ReactElement
  mutationName: DocumentNode
}

const getActionSubtitle = (action: string, count: number) =>
  `Are you sure you want to ${action} ${count > 1 ? `these ${count} discount codes` : 'this discount code'}?`

const BULK_ACTION_STOP: BulkAction = {
  id: 'stop',
  title: 'Stop',
  getTitle: () => 'Stop',
  getSubtitle: (count?: number) => getActionSubtitle('stop', count ?? 1),
  mutationName: BULK_UPDATE_PROMO_CODES,
}

const BULK_ACTION_DELETE: BulkAction = {
  id: 'delete',
  title: 'Delete',
  getTitle: () => 'Permanently Delete',
  getSubtitle: (count?: number) => getActionSubtitle('permanently delete', count ?? 1),
  mutationName: BULK_REMOVE_PROMO_CODES,
}

const BULK_ACTIONS_ALL: BulkAction[] = [BULK_ACTION_STOP, BULK_ACTION_DELETE]
const BULK_ACTIONS_EXPIRED: BulkAction[] = [BULK_ACTION_DELETE]

const TABS: Array<{
  key: keyof Omit<ReturnType<typeof usePromoCodesCount>, 'refetch'>
  title: string
  value: PromoCodeFilters['status']
}> = [
  { key: 'all', title: 'All', value: undefined },
  { key: 'active', title: 'Active', value: PromocodeStatusEnum.Active },
  { key: 'expired', title: 'Expired', value: PromocodeStatusEnum.Expired },
]

type Props = {
  platform: Platform
}

export const List = withPlatformCheck(({ platform }: Props) => {
  const { mutate } = useApolloClient()
  const { getLink } = useLink()
  const { data: userData, loading: loadingUser } = useQuery<findMyUser>(FIND_MY_USER)
  const [confirmItemAction, setConfirmItemAction] = useState<{ action: BulkAction; item: PromoCode } | null>(null)
  const [confirmBulkAction, setConfirmBulkAction] = useState<BulkAction | null>(null)
  const [selectedItems, setSelectedItems] = useState<PromoCode[]>([])
  const promoCodesCount = usePromoCodesCount(platform.id)
  const { items, count, loading, loaded, refetch, pagination, search, order, filters, onFilterChange } = usePromoCodes()
  const isExpiredSelected = filters.status === PromocodeStatusEnum.Expired

  const refetchVariables = {
    filters: {
      ...filters,
      platform: {
        id: platform.id,
      },
    },
  }

  useEffect(() => {
    refetch(refetchVariables)
  }, [refetch])

  useEffect(() => {
    if (!loading) {
      setConfirmItemAction(null)
      setConfirmBulkAction(null)
      setSelectedItems([])
    }
  }, [loading])

  const applyBulkAction = useCallback(
    async (action: BulkAction, items: PromoCode[]) => {
      if (action.id === 'stop') {
        const notExpiredAndNotUsedItems = items.filter(
          (promoCode) =>
            promoCode.status !== PromocodeStatusEnum.Expired &&
            (promoCode.maxUses == undefined || promoCode.maxUses - promoCode.alreadyUsed > 0)
        )
        if (!notExpiredAndNotUsedItems.length) {
          return
        }

        await mutate<bulkUpdatePromoCodes, bulkUpdatePromoCodesVariables>({
          mutation: action.mutationName,
          variables: {
            items: notExpiredAndNotUsedItems.map(({ id }) => ({
              id,
              status: PromocodeStatusEnum.Expired,
              expirationDate: addMinutes(new Date(), 1).toISOString(),
              platform: {
                id: platform.id,
              },
            })),
          },
        })
      } else {
        await mutate({
          mutation: action.mutationName,
          variables: {
            ids: items.map(({ id }) => ({ id })),
          },
        })
      }
    },
    [platform.id]
  )

  const handleApplyBulkAction = useCallback(async () => {
    if (!selectedItems.length || !confirmBulkAction) {
      return
    }

    setConfirmBulkAction(null)
    setSelectedItems([])
    try {
      await applyBulkAction(confirmBulkAction, selectedItems)

      toast.success(`${confirmBulkAction.title} action has been successfully applied`)
    } catch (e) {
      toast.error(`An error occurred while applying ${confirmBulkAction.title} action`)
    }
    refetch(refetchVariables)
    promoCodesCount.refetch()
  }, [confirmBulkAction, selectedItems, refetch, promoCodesCount.refetch])

  const handleApplyItemAction = useCallback(async () => {
    if (!confirmItemAction) {
      return
    }

    setConfirmItemAction(null)

    try {
      await applyBulkAction(confirmItemAction.action, [confirmItemAction.item])

      toast.success(`${confirmItemAction.action.title} action has been successfully applied`)
    } catch (e) {
      toast.error(`An error occurred while applying ${confirmItemAction.action.title} action`)
    }
    refetch(refetchVariables)
    promoCodesCount.refetch()
  }, [confirmItemAction, refetch, userData, promoCodesCount.refetch])

  const getActionButton = useCallback(
    (action: BulkAction, item: PromoCode) => (
      <Button
        variant="text"
        size="small"
        color="blue"
        css={classes.action}
        onClick={() => setConfirmItemAction({ action, item })}
        disabled={loading}
      >
        {action.title}
      </Button>
    ),
    [setConfirmItemAction, loading]
  )

  if (!loaded || loadingUser) {
    return (
      <div css={classes.headerWrapper}>
        <PageLoader />
      </div>
    )
  }

  return (
    <div css={classes.wrapper}>
      <div css={classes.headerWrapper}>
        <Typography variant="title3" css={classes.title}>
          Discount Codes
        </Typography>
        <Link to={getLink(cmsRoutes.PROMO_CODES_CREATE)}>
          <Button color="blue" variant="contained">
            Add Discount
          </Button>
        </Link>
      </div>
      <div css={classes.toolbarWrapper}>
        <div css={classes.tabsWrapper}>
          {TABS.map(({ key, title, value }) => (
            <Button
              key={key}
              size="small"
              variant="text"
              onClick={() => onFilterChange('status', value)}
              color={filters.status !== value ? 'gray' : 'blue'}
              css={classes.action}
            >
              {title} ({promoCodesCount[key]})
            </Button>
          ))}
        </div>
        <Search {...search} css={classes.search} />
      </div>
      <HeaderWrapper css={classes.filters}>
        <FiltersWrapper className="wrapper">
          <BulkActions
            bulkActions={isExpiredSelected ? BULK_ACTIONS_EXPIRED : BULK_ACTIONS_ALL}
            onApply={setConfirmBulkAction}
            disabled={!selectedItems.length || loading}
          />
        </FiltersWrapper>
        <PaginationPage totalCount={count} {...pagination} />
      </HeaderWrapper>
      <Table
        css={classes.tableWrapper}
        columns={[
          {
            title: 'Name',
            renderCell: (promoCode) => {
              const hasStopAction =
                !isExpiredSelected &&
                promoCode.status !== PromocodeStatusEnum.Expired &&
                (promoCode.maxUses == undefined || promoCode.maxUses - promoCode.alreadyUsed > 0)

              return (
                <div css={classes.titleWrapper}>
                  <div>{promoCode.name}</div>
                  <div className="actions" css={[classes.actions, hasStopAction && classes.largeActions]}>
                    <Link
                      css={classes.actionLink}
                      to={getLink(cmsRoutes.PROMO_CODES_EDIT, { promoCodeId: promoCode.id })}
                    >
                      <Button variant="text" size="small" color="blue" css={classes.action} disabled={loading}>
                        Edit
                      </Button>
                    </Link>
                    {hasStopAction && getActionButton(BULK_ACTION_STOP, promoCode)}
                    {getActionButton(BULK_ACTION_DELETE, promoCode)}
                    <Link
                      css={classes.actionLink}
                      to={getLink(cmsRoutes.PROMO_CODES_ANALYTICS, { promoCodeId: promoCode.id })}
                    >
                      <Button variant="text" size="small" color="blue" css={classes.action} disabled={loading}>
                        Analytics
                      </Button>
                    </Link>
                  </div>
                </div>
              )
            },
            flex: 2,
          },
          {
            title: 'Description',
            renderCell: (promoCode) => promoCode.description || '-',
            flex: 2,
          },
          {
            title: 'Code',
            renderCell: (promoCode) => promoCode.code,
            flex: 2,
          },
          {
            title: 'Level',
            renderCell: (promoCode) =>
              (promoCode.product?.length || 0) + (promoCode.additionalTest?.length || 0) ? 'Multiple' : 'Single',
          },
          {
            title: 'Amount',
            renderCell: (promoCode) => {
              if (promoCode.type === PromocodeTypeEnum.Percentage) {
                return `${convertFromSavedPercentage(promoCode.amount)}%`
              }

              return `$${convertToDollars(promoCode.amount)}`
            },
          },
          {
            title: 'Status',
            renderCell: (promoCode) => promoCode.status,
          },
          {
            title: 'Uses',
            renderCell: (promoCode) => String(promoCode.alreadyUsed),
            orderField: 'alreadyUsed',
          },
          {
            title: 'Uses Left',
            renderCell: (promoCode) =>
              String(promoCode.maxUses == undefined || promoCode.maxUses - promoCode.alreadyUsed > 0 ? 'Yes' : 'No'),
          },
          {
            title: 'Expiration',
            renderCell: (promoCode) =>
              promoCode.expirationDate ? getTimeZoneAgnosticDate(promoCode.expirationDate).toLocaleDateString() : '-',
            flex: '0 1 124px',
          },
        ]}
        items={items}
        selectedItems={selectedItems}
        order={order.value}
        onOrder={order.onChange}
        onSelect={setSelectedItems}
        isSelectable
        wrapperMinWidth={1176}
      />
      <FooterWrapper>
        <PaginationTake {...pagination} />
      </FooterWrapper>
      <ConfirmationModal
        open={Boolean(confirmBulkAction && selectedItems.length)}
        title={confirmBulkAction?.getTitle(selectedItems.length) ?? ''}
        subtitle={confirmBulkAction?.getSubtitle(selectedItems.length) ?? ''}
        onClose={() => setConfirmBulkAction(null)}
        onConfirm={handleApplyBulkAction}
      />
      <ConfirmationModal
        open={Boolean(confirmItemAction)}
        title={confirmItemAction?.action.getTitle() ?? ''}
        subtitle={confirmItemAction?.action.getSubtitle() ?? ''}
        onClose={() => setConfirmItemAction(null)}
        onConfirm={handleApplyItemAction}
      />
    </div>
  )
})
